import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Settings, Plus, Edit2, Trash2, Building2, AlertCircle, CheckCircle2, X, Loader2, Tag, Hash, Zap, Search, ScanText } from 'lucide-react';
import { matchRulesService, BankMatchRule } from '../../lib/matchRulesService';
import { recurringActionsService } from '../../lib/recurringActionsService';
import { RecurringActionLine } from '../../types/database';
import { useAuth } from '../../features/auth';
import { useCompany } from '../../context/CompanyContext';
import { bankAccountsService, CreateBankAccountData } from '../../lib/bankAccountsService';
import { accountingCategoriesService, CreateAccountingCategoryData } from '../../lib/accountingCategoriesService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { BankAccount, AccountingCategory, CashflowMapping, OffsetCode } from '../../types/database';
import { teamService, TeamMember } from '../../lib/teamService';
import { OffsetCodeModal, OffsetCodeFormData } from '../../components/modals/OffsetCodeModal';
import { CategoryRulesModal } from '../../components/modals/CategoryRulesModal';

type TabType = 'banks' | 'categories' | 'offsets' | 'match-rules';

export function CashflowSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('banks');

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [accountingCategories, setAccountingCategories] = useState<AccountingCategory[]>([]);
  const [offsetCodes, setOffsetCodes] = useState<OffsetCode[]>([]);
  const [matchRules, setMatchRules] = useState<BankMatchRule[]>([]);
  const [recurringLines, setRecurringLines] = useState<RecurringActionLine[]>([]);
  const [matchRuleForm, setMatchRuleForm] = useState({ search_text: '', target_recurring_line_id: '' });
  const [savingRule, setSavingRule] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showOffsetModal, setShowOffsetModal] = useState(false);
  // Category auto-detection rules modal
  const [rulesModalCategory, setRulesModalCategory] = useState<{ id: string; name: string } | null>(null);
  const [editingOffset, setEditingOffset] = useState<OffsetCode | null>(null);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [editingCategory, setEditingCategory] = useState<AccountingCategory | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Bank Account Form state
  const [formData, setFormData] = useState({
    nickname: '',
    bank_name: '',
    account_number: '',
    branch_number: '',
    routing_number: '',
    currency: 'ILS',
    is_active: true,
    account_type: 'checking' as 'checking' | 'credit_card',
    linked_checking_account_id: '',
    card_provider: '',
  });

  // Accounting Category Form state
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    cashflow_mapping: '' as CashflowMapping | '',
  });

  useEffect(() => {
    if (activeCompanyId) {
      loadData();
    }
  }, [user, activeCompanyId]);

  const loadData = async () => {
    if (!user || !activeCompanyId) return;

    setLoading(true);
    setError(null);

    try {
      const companyId = activeCompanyId;

      // Set company from context
      if (activeCompany) {
        setCompany(activeCompany);
      }

      // Get user's role
      const { data: membersData } = await teamService.getTeamMembers(companyId);
      const currentMember = membersData?.find((m: TeamMember) => m.user_id === user.id);
      setUserRole(currentMember?.role || null);

      // Get bank accounts
      const { data: accountsData, error: accountsError } = await bankAccountsService.getBankAccounts(companyId);
      if (accountsError) {
        console.error('Load bank accounts error:', accountsError);
        setError('Failed to load bank accounts');
        return;
      }
      setBankAccounts(accountsData || []);

      // Get accounting categories
      const { data: categoriesData, error: categoriesError } = await accountingCategoriesService.getAccountingCategories(companyId);
      if (categoriesError) {
        console.error('Load accounting categories error:', categoriesError);
        // Don't fail the whole page if categories fail to load
      }
      setAccountingCategories(categoriesData || []);

      // Get offset codes
      const { data: offsetsData, error: offsetsError } = await offsetCodesService.getOffsetCodes(companyId);
      if (offsetsError) {
        console.error('Load offset codes error:', offsetsError);
        // Don't fail the whole page if offset codes fail to load
      }
      setOffsetCodes(offsetsData || []);

      // Get match rules
      const { data: rulesData } = await matchRulesService.getMatchRules(companyId);
      setMatchRules(rulesData || []);

      // Get recurring lines (for the dropdown in the rules form)
      const { data: recurringData } = await recurringActionsService.getRecurringActions(companyId);
      setRecurringLines(recurringData || []);
    } catch (err) {
      console.error('Load data error:', err);
      setError('An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = userRole === 'admin';

  // ── Auto-match rules handlers ──────────────────────────────────────
  const handleAddMatchRule = async () => {
    if (!activeCompanyId || !matchRuleForm.search_text.trim() || !matchRuleForm.target_recurring_line_id) return;
    setSavingRule(true);
    setError(null);
    try {
      const { data, error: ruleError } = await matchRulesService.createMatchRule({
        company_id: activeCompanyId,
        search_text: matchRuleForm.search_text.trim(),
        target_recurring_line_id: matchRuleForm.target_recurring_line_id,
      });
      if (ruleError) { setError(ruleError.message); return; }
      if (data) setMatchRules(prev => [data, ...prev]);
      setMatchRuleForm({ search_text: '', target_recurring_line_id: '' });
      setSuccess('חוק התאמה נוסף בהצלחה');
      setTimeout(() => setSuccess(null), 3000);
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteMatchRule = async (ruleId: string) => {
    if (!window.confirm('האם למחוק את חוק ההתאמה?')) return;
    const { error: ruleError } = await matchRulesService.deleteMatchRule(ruleId);
    if (ruleError) { setError(ruleError.message); return; }
    setMatchRules(prev => prev.filter(r => r.id !== ruleId));
    setSuccess('חוק נמחק');
    setTimeout(() => setSuccess(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      nickname: '',
      bank_name: '',
      account_number: '',
      branch_number: '',
      routing_number: '',
      currency: company?.country === 'US' ? 'USD' : company?.country === 'CA' ? 'CAD' : 'ILS',
      is_active: true,
      account_type: 'checking',
      linked_checking_account_id: '',
      card_provider: '',
    });
    setEditingAccount(null);
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      cashflow_mapping: '',
    });
    setEditingCategory(null);
  };

  const handleAddClick = () => {
    resetForm();
    setShowAddModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditClick = (account: BankAccount) => {
    setEditingAccount(account);
    setFormData({
      nickname: account.nickname,
      bank_name: account.bank_name,
      account_number: account.account_number,
      branch_number: account.branch_number || '',
      routing_number: account.routing_number || '',
      currency: account.currency,
      is_active: account.is_active,
      account_type: account.account_type || 'checking',
      linked_checking_account_id: account.linked_checking_account_id || '',
      card_provider: account.card_provider || '',
    });
    setShowAddModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    resetForm();
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!company) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingAccount) {
        // Update existing account
        const { error: updateError } = await bankAccountsService.updateBankAccount(
          editingAccount.id,
          {
            nickname: formData.nickname,
            bank_name: formData.bank_name,
            account_number: formData.account_number,
            branch_number: formData.branch_number || null,
            routing_number: formData.routing_number || null,
            currency: formData.currency,
            is_active: formData.is_active,
            account_type: formData.account_type,
            linked_checking_account_id: formData.linked_checking_account_id || null,
            card_provider: formData.card_provider || null,
          }
        );

        if (updateError) {
          setError(t('cashflow.settings.bankAccounts.messages.updateError'));
          return;
        }

        setSuccess(t('cashflow.settings.bankAccounts.messages.updateSuccess'));
      } else {
        // Create new account
        const accountData: CreateBankAccountData = {
          company_id: activeCompanyId,
          nickname: formData.nickname,
          bank_name: formData.bank_name,
          account_number: formData.account_number,
          branch_number: formData.branch_number || null,
          routing_number: formData.routing_number || null,
          currency: formData.currency,
          is_active: formData.is_active,
          account_type: formData.account_type,
          linked_checking_account_id: formData.linked_checking_account_id || null,
          card_provider: formData.card_provider || null,
        };

        const { error: createError } = await bankAccountsService.createBankAccount(accountData);

        if (createError) {
          setError(t('cashflow.settings.bankAccounts.messages.createError'));
          return;
        }

        setSuccess(t('cashflow.settings.bankAccounts.messages.createSuccess'));
      }

      // Close modal and reload data
      setShowAddModal(false);
      resetForm();
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Submit error:', err);
      setError(editingAccount 
        ? t('cashflow.settings.bankAccounts.messages.updateError')
        : t('cashflow.settings.bankAccounts.messages.createError')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!window.confirm(t('cashflow.settings.bankAccounts.messages.deleteConfirm'))) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await bankAccountsService.deleteBankAccount(accountId);

      if (deleteError) {
        setError(t('cashflow.settings.bankAccounts.messages.deleteError'));
        return;
      }

      setSuccess(t('cashflow.settings.bankAccounts.messages.deleteSuccess'));
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete error:', err);
      setError(t('cashflow.settings.bankAccounts.messages.deleteError'));
    }
  };

  // ===== Accounting Category Handlers =====
  const handleAddCategoryClick = () => {
    resetCategoryForm();
    setShowCategoryModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditCategoryClick = (category: AccountingCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      cashflow_mapping: category.cashflow_mapping,
    });
    setShowCategoryModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    resetCategoryForm();
    setError(null);
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!company || !categoryFormData.cashflow_mapping) {
      setError('All fields are required');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingCategory) {
        // Update existing category
        const { error: updateError } = await accountingCategoriesService.updateAccountingCategory(
          editingCategory.id,
          {
            name: categoryFormData.name,
            cashflow_mapping: categoryFormData.cashflow_mapping,
          }
        );

        if (updateError) {
          setError('Failed to update accounting category');
          return;
        }

        setSuccess('Accounting category updated successfully!');
      } else {
        // Create new category
        const categoryData: CreateAccountingCategoryData = {
          company_id: activeCompanyId,
          name: categoryFormData.name,
          cashflow_mapping: categoryFormData.cashflow_mapping,
        };

        const { error: createError } = await accountingCategoriesService.createAccountingCategory(categoryData);

        if (createError) {
          setError('Failed to create accounting category');
          return;
        }

        setSuccess('Accounting category created successfully!');
      }

      // Close modal and reload data
      setShowCategoryModal(false);
      resetCategoryForm();
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Category submit error:', err);
      setError(editingCategory 
        ? 'Failed to update accounting category'
        : 'Failed to create accounting category'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this accounting category?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await accountingCategoriesService.deleteAccountingCategory(categoryId);

      if (deleteError) {
        setError('Failed to delete accounting category');
        return;
      }

      setSuccess('Accounting category deleted successfully!');
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete category error:', err);
      setError('Failed to delete accounting category');
    }
  };

  // ===== Offset Code Handlers =====
  const handleAddOffsetClick = () => {
    setEditingOffset(null);
    setShowOffsetModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditOffsetClick = (offset: OffsetCode) => {
    setEditingOffset(offset);
    setShowOffsetModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleCloseOffsetModal = () => {
    setShowOffsetModal(false);
    setEditingOffset(null);
    setError(null);
  };

  const handleOffsetSubmit = async (data: OffsetCodeFormData, id?: string) => {
    if (!company) {
      setError('Company information not available');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (id) {
        // Update existing offset code
        console.log('Updating offset code:', { id, data });

        const { error: updateError } = await offsetCodesService.updateOffsetCode(id, {
          code_number: data.code_number,
          name: data.name,
          frequency_type: data.frequency_type,
        });

        if (updateError) {
          console.error('Offset code update error:', updateError);
          setError(`שגיאה בעדכון חוק קיזוז: ${updateError.message}`);
          return;
        }

        setSuccess('חוק קיזוז עודכן בהצלחה!');
      } else {
        // Create new offset code
        console.log('Submitting offset code with data:', {
          company_id: activeCompanyId,
          code_number: data.code_number,
          name: data.name,
          frequency_type: data.frequency_type,
        });

        const { error: createError } = await offsetCodesService.createOffsetCode({
          company_id: activeCompanyId,
          code_number: data.code_number,
          name: data.name,
          frequency_type: data.frequency_type,
        });

        if (createError) {
          console.error('Offset code creation error:', createError);
          setError(`שגיאה ביצירת חוק קיזוז: ${createError.message}`);
          return;
        }

        setSuccess('חוק קיזוז נוצר בהצלחה!');
      }

      setShowOffsetModal(false);
      setEditingOffset(null);
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Offset code submit error:', err);
      setError(id ? 'שגיאה בעדכון חוק קיזוז' : 'שגיאה ביצירת חוק קיזוז');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleOffsetActive = async (offsetId: string, currentStatus: boolean) => {
    setError(null);
    setSuccess(null);

    try {
      const newStatus = !currentStatus;
      const { error: toggleError } = await offsetCodesService.toggleOffsetCodeActive(offsetId, newStatus);

      if (toggleError) {
        console.error('Toggle offset active error:', toggleError);
        setError(`שגיאה בשינוי סטטוס: ${toggleError.message}`);
        return;
      }

      setSuccess(newStatus ? 'חוק קיזוז הופעל בהצלחה!' : 'חוק קיזוז הושבת בהצלחה!');
      await loadData();

      // Clear success message after 2 seconds
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Toggle offset active error:', err);
      setError('שגיאה בשינוי סטטוס');
    }
  };

  const handleDeleteOffset = async (offsetId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק חוק קיזוז זה?')) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const { error: deleteError } = await offsetCodesService.deleteOffsetCode(offsetId);

      if (deleteError) {
        setError('שגיאה במחיקת חוק קיזוז');
        return;
      }

      setSuccess('חוק קיזוז נמחק בהצלחה!');
      await loadData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete offset code error:', err);
      setError('שגיאה במחיקת חוק קיזוז');
    }
  };

  // Get dynamic field labels based on company country
  const getDynamicFields = () => {
    const country = company?.country;

    switch (country) {
      case 'IL': // Israel
        return {
          showBranch: true,
          showRouting: false,
          branchLabel: t('cashflow.settings.bankAccounts.form.branchNumber'),
          branchPlaceholder: t('cashflow.settings.bankAccounts.form.branchNumberPlaceholder'),
          routingLabel: '',
          routingPlaceholder: '',
        };
      case 'US': // United States
        return {
          showBranch: false,
          showRouting: true,
          branchLabel: '',
          branchPlaceholder: '',
          routingLabel: t('cashflow.settings.bankAccounts.form.routingNumber'),
          routingPlaceholder: t('cashflow.settings.bankAccounts.form.routingNumberPlaceholder'),
        };
      case 'CA': // Canada
        return {
          showBranch: true,
          showRouting: true,
          branchLabel: t('cashflow.settings.bankAccounts.form.transitNumber'),
          branchPlaceholder: t('cashflow.settings.bankAccounts.form.transitNumberPlaceholder'),
          routingLabel: t('cashflow.settings.bankAccounts.form.institutionNumber'),
          routingPlaceholder: t('cashflow.settings.bankAccounts.form.institutionNumberPlaceholder'),
        };
      default: // Other countries
        return {
          showBranch: true,
          showRouting: true,
          branchLabel: t('cashflow.settings.bankAccounts.form.branchNumber'),
          branchPlaceholder: t('cashflow.settings.bankAccounts.form.branchNumberPlaceholder'),
          routingLabel: t('cashflow.settings.bankAccounts.form.routingNumber'),
          routingPlaceholder: t('cashflow.settings.bankAccounts.form.routingNumberPlaceholder'),
        };
    }
  };

  const dynamicFields = getDynamicFields();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Settings className="h-6 w-6 text-primary-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('cashflow.settings.title')}
          </h1>
        </div>
        <p className="text-gray-600 mt-2">
          {t('cashflow.settings.description')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 rounded-md bg-green-50 border border-green-200 p-4">
          <div className="flex">
            <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-green-500 hover:bg-green-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-red-500 hover:bg-red-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Settings tabs">
          <button
            onClick={() => setActiveTab('banks')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${
                activeTab === 'banks'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === 'banks' ? 'page' : undefined}
          >
            <Building2
              className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'banks' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              aria-hidden="true"
            />
            הגדרות בנקים
          </button>
          
          <button
            onClick={() => setActiveTab('categories')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${
                activeTab === 'categories'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === 'categories' ? 'page' : undefined}
          >
            <Tag
              className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'categories' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              aria-hidden="true"
            />
            הגדרות סיווג תזרים וחשבונאות
          </button>

          <button
            onClick={() => setActiveTab('offsets')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${
                activeTab === 'offsets'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === 'offsets' ? 'page' : undefined}
          >
            <Hash
              className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'offsets' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              aria-hidden="true"
            />
            חוקי קיזוז
          </button>

          <button
            onClick={() => setActiveTab('match-rules')}
            className={`
              group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              transition-colors duration-200
              ${
                activeTab === 'match-rules'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
            aria-current={activeTab === 'match-rules' ? 'page' : undefined}
          >
            <Zap
              className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === 'match-rules' ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
              `}
              aria-hidden="true"
            />
            חוקי התאמה אוטומטית
          </button>
        </nav>
      </div>

      {/* Tab Content - Bank Settings */}
      {activeTab === 'banks' && (
        <>
          {/* Bank Accounts Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t('cashflow.settings.bankAccounts.title')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('cashflow.settings.bankAccounts.description')}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={handleAddClick}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('cashflow.settings.bankAccounts.addButton')}
            </button>
          )}
        </div>

        {/* Bank Accounts Table */}
        {bankAccounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <Building2 className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('cashflow.settings.bankAccounts.emptyState.title')}
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              {t('cashflow.settings.bankAccounts.emptyState.description')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.nickname')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.type', 'סוג')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.bankName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.accountNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.currency')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('cashflow.settings.bankAccounts.table.status')}
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('cashflow.settings.bankAccounts.table.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bankAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{account.nickname}</div>
                      {account.account_type === 'credit_card' && account.card_provider && (
                        <div className="text-xs text-gray-400">{account.card_provider}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        account.account_type === 'credit_card'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-50 text-gray-700'
                      }`}>
                        {account.account_type === 'credit_card'
                          ? t('cashflow.settings.bankAccounts.type.creditCard', 'כרטיס אשראי')
                          : t('cashflow.settings.bankAccounts.type.checking', 'עו"ש')}
                      </span>
                      {account.account_type === 'credit_card' && (() => {
                        const linked = bankAccounts.find(a => a.id === account.linked_checking_account_id);
                        return linked ? (
                          <div className="text-xs text-gray-400 mt-0.5">← {linked.nickname}</div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{account.bank_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{account.account_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{account.currency}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          account.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {account.is_active
                          ? t('cashflow.settings.bankAccounts.status.active')
                          : t('cashflow.settings.bankAccounts.status.inactive')}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEditClick(account)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            {t('cashflow.settings.bankAccounts.editButton')}
                          </button>
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {t('cashflow.settings.bankAccounts.deleteButton')}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Bank Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingAccount
                    ? t('cashflow.settings.bankAccounts.form.editTitle')
                    : t('cashflow.settings.bankAccounts.form.title')}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cashflow.settings.bankAccounts.form.accountType', 'סוג חשבון')}
                </label>
                <div className="flex gap-4">
                  <label className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.account_type === 'checking' ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="account_type"
                      value="checking"
                      checked={formData.account_type === 'checking'}
                      onChange={() => setFormData({ ...formData, account_type: 'checking', linked_checking_account_id: '', card_provider: '' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm font-medium">{t('cashflow.settings.bankAccounts.type.checking', 'עו"ש')}</span>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.account_type === 'credit_card' ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="account_type"
                      value="credit_card"
                      checked={formData.account_type === 'credit_card'}
                      onChange={() => setFormData({ ...formData, account_type: 'credit_card' })}
                      className="text-primary-600"
                    />
                    <span className="text-sm font-medium">{t('cashflow.settings.bankAccounts.type.creditCard', 'כרטיס אשראי')}</span>
                  </label>
                </div>
              </div>

              {/* Linked Checking Account (only for credit cards) */}
              {formData.account_type === 'credit_card' && (
                <div>
                  <label htmlFor="linked_checking_account_id" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cashflow.settings.bankAccounts.form.linkedAccount', 'חשבון עו"ש משויך')} *
                  </label>
                  <select
                    id="linked_checking_account_id"
                    value={formData.linked_checking_account_id}
                    onChange={(e) => setFormData({ ...formData, linked_checking_account_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">{t('cashflow.settings.bankAccounts.form.selectLinkedAccount', 'בחר חשבון עו"ש...')}</option>
                    {bankAccounts
                      .filter(a => a.account_type === 'checking' && a.id !== editingAccount?.id)
                      .map(a => (
                        <option key={a.id} value={a.id}>{a.nickname} - {a.bank_name}</option>
                      ))
                    }
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {t('cashflow.settings.bankAccounts.form.linkedAccountHint', 'חשבון הבנק שממנו יורד חיוב הכרטיס מדי חודש')}
                  </p>
                </div>
              )}

              {/* Card Provider (only for credit cards) */}
              {formData.account_type === 'credit_card' && (
                <div>
                  <label htmlFor="card_provider" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cashflow.settings.bankAccounts.form.cardProvider', 'ספק כרטיס')}
                  </label>
                  <select
                    id="card_provider"
                    value={formData.card_provider}
                    onChange={(e) => setFormData({ ...formData, card_provider: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t('cashflow.settings.bankAccounts.form.selectProvider', 'בחר ספק...')}</option>
                    <option value="Visa">Visa</option>
                    <option value="Mastercard">Mastercard</option>
                    <option value="Amex">American Express</option>
                    <option value="Isracard">ישראכרט</option>
                    <option value="Cal">כאל (CAL)</option>
                    <option value="Max">מקס (Max)</option>
                    <option value="Diners">Diners</option>
                    <option value="Other">{t('cashflow.settings.bankAccounts.form.otherProvider', 'אחר')}</option>
                  </select>
                </div>
              )}

              {/* Nickname */}
              <div>
                <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cashflow.settings.bankAccounts.form.nickname')}
                </label>
                <input
                  type="text"
                  id="nickname"
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                  placeholder={t('cashflow.settings.bankAccounts.form.nicknamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('cashflow.settings.bankAccounts.form.nicknameHint')}
                </p>
              </div>

              {/* Bank Name */}
              <div>
                <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cashflow.settings.bankAccounts.form.bankName')}
                </label>
                <input
                  type="text"
                  id="bank_name"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder={t('cashflow.settings.bankAccounts.form.bankNamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Account Number */}
              <div>
                <label htmlFor="account_number" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cashflow.settings.bankAccounts.form.accountNumber')}
                </label>
                <input
                  type="text"
                  id="account_number"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder={t('cashflow.settings.bankAccounts.form.accountNumberPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Dynamic Fields Based on Country */}
              {dynamicFields.showBranch && (
                <div>
                  <label htmlFor="branch_number" className="block text-sm font-medium text-gray-700 mb-1">
                    {dynamicFields.branchLabel}
                  </label>
                  <input
                    type="text"
                    id="branch_number"
                    value={formData.branch_number}
                    onChange={(e) => setFormData({ ...formData, branch_number: e.target.value })}
                    placeholder={dynamicFields.branchPlaceholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {dynamicFields.showRouting && (
                <div>
                  <label htmlFor="routing_number" className="block text-sm font-medium text-gray-700 mb-1">
                    {dynamicFields.routingLabel}
                  </label>
                  <input
                    type="text"
                    id="routing_number"
                    value={formData.routing_number}
                    onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                    placeholder={dynamicFields.routingPlaceholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Currency */}
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cashflow.settings.bankAccounts.form.currency')}
                </label>
                <select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="ILS">ILS (₪)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="CAD">CAD ($)</option>
                </select>
              </div>

              {/* Is Active Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  {t('cashflow.settings.bankAccounts.form.isActive')}
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('cashflow.settings.bankAccounts.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {t('cashflow.settings.bankAccounts.form.submitting')}
                    </>
                  ) : (
                    t('cashflow.settings.bankAccounts.form.submit')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}

      {/* Tab Content - Accounting Categories */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Tag className="h-5 w-5" />
                סיווגים חשבונאיים
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                הגדר סיווגים חשבונאיים המקושרים לסיווגי תזרים קבועים
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={handleAddCategoryClick}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                הוסף סיווג
              </button>
            )}
          </div>

          {/* Accounting Categories Table */}
          {accountingCategories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Tag className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                אין סיווגים חשבונאיים
              </h3>
              <p className="text-gray-600 text-center max-w-md">
                הוסף סיווג חשבונאי ראשון כדי להתחיל
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      שם הסיווג
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      סיווג תזרים
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        פעולות
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accountingCategories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getCashflowMappingLabel(category.cashflow_mapping)}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                          <div className="flex items-center gap-2">
                            {/* Auto-detect rules button */}
                            <button
                              onClick={() => setRulesModalCategory({ id: category.id, name: category.name })}
                              title="כללי זיהוי אוטומטי"
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium
                                border border-indigo-300 text-indigo-700 bg-white rounded hover:bg-indigo-50
                                focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
                            >
                              <ScanText className="h-3.5 w-3.5" />
                              כללי זיהוי
                            </button>
                            <button
                              onClick={() => handleEditCategoryClick(category)}
                              className="text-primary-600 hover:text-primary-900"
                              title="ערוך"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="text-red-600 hover:text-red-900"
                              title="מחק"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Category Auto-Detection Rules Modal */}
          {rulesModalCategory && activeCompanyId && (
            <CategoryRulesModal
              isOpen={!!rulesModalCategory}
              onClose={() => setRulesModalCategory(null)}
              companyId={activeCompanyId}
              categoryId={rulesModalCategory.id}
              categoryName={rulesModalCategory.name}
            />
          )}

          {/* Add/Edit Category Modal */}
          {showCategoryModal && (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingCategory ? 'ערוך סיווג חשבונאי' : 'הוסף סיווג חשבונאי'}
                    </h3>
                    <button
                      onClick={handleCloseCategoryModal}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleCategorySubmit} className="px-6 py-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      שם סיווג חשבונאי <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="לדוגמה: שכירות משרדים"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      שיוך לתזרים <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={categoryFormData.cashflow_mapping}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, cashflow_mapping: e.target.value as CashflowMapping })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">בחר סיווג תזרים</option>
                      <option value="current_business">שוטף - פעילות עסקית</option>
                      <option value="financing_loan">מימון - פעילות הלוואה</option>
                      <option value="intra_group_loan">הלוואה בתוך הקבוצה</option>
                      <option value="investment">השקעה</option>
                      <option value="savings">חיסכון</option>
                      <option value="taxes">מיסים</option>
                      <option value="inter_account_transfer">העברות בין חשבונות החברה</option>
                      <option value="other">אחר</option>
                      <option value="personal">אישי</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleCloseCategoryModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      ביטול
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 inline-flex items-center"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          שומר...
                        </>
                      ) : (
                        'שמור'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Content - Offset Codes */}
      {activeTab === 'offsets' && (
        <>
          {/* Offset Codes Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Hash className="h-5 w-5" />
                  חוקי קיזוז
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  ניהול חוקי קיזוז עם הגדרות תדירות
                </p>
              </div>
              {isAdmin && (
                <button
                  onClick={handleAddOffsetClick}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  הוסף חוק קיזוז
                </button>
              )}
            </div>

            {/* Offset Codes Table */}
            {offsetCodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <Hash className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  אין חוקי קיזוז
                </h3>
                <p className="text-gray-600 text-center max-w-md">
                  התחל בהוספת חוקי קיזוז עם הגדרות תדירות מותאמות אישית
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        מספר קיזוז
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        שם
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        תדירות
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        סטטוס
                      </th>
                      {isAdmin && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          פעולות
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {offsetCodes.map((offset) => (
                      <tr key={offset.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm font-medium text-gray-900">{offset.code_number}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="text-sm text-gray-900">{offset.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {getFrequencyTypeLabel(offset.frequency_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleToggleOffsetActive(offset.id, offset.is_active)}
                            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                            style={{ backgroundColor: offset.is_active ? '#10b981' : '#d1d5db' }}
                            title={offset.is_active ? 'פעיל - לחץ להשבתה' : 'מושבת - לחץ להפעלה'}
                          >
                            <span
                              className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                              style={{ transform: offset.is_active ? 'translateX(-1.5rem)' : 'translateX(-0.25rem)' }}
                            />
                          </button>
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditOffsetClick(offset)}
                                className="inline-flex items-center px-3 py-1.5 border border-primary-300 shadow-sm text-xs font-medium rounded text-primary-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                ערוך
                              </button>
                              <button
                                onClick={() => handleDeleteOffset(offset.id)}
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                מחק
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Add Offset Code Modal */}
          <OffsetCodeModal
            isOpen={showOffsetModal}
            onClose={handleCloseOffsetModal}
            onSave={handleOffsetSubmit}
            isSaving={submitting}
            editingOffset={editingOffset}
          />
        </>
      )}

      {/* Tab Content – Auto-Match Rules */}
      {activeTab === 'match-rules' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Zap className="h-5 w-5 text-yellow-500" />
                חוקי התאמה אוטומטית
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                הגדר חוקים שיתאמו אוטומטית תנועות בנק לפעולות חוזרות לפי מחרוזת חיפוש
              </p>
            </div>
          </div>

          {/* Add Rule Form */}
          {isAdmin && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">הוסף חוק חדש</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    טקסט חיפוש (בתיאור תנועת הבנק)
                  </label>
                  <input
                    type="text"
                    value={matchRuleForm.search_text}
                    onChange={e => setMatchRuleForm(f => ({ ...f, search_text: e.target.value }))}
                    placeholder='לדוגמה: "חברת חשמל"'
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    פעולה חוזרת מטרה
                  </label>
                  <select
                    value={matchRuleForm.target_recurring_line_id}
                    onChange={e => setMatchRuleForm(f => ({ ...f, target_recurring_line_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- בחר פעולה חוזרת --</option>
                    {/* Deduplicated: one entry per unique (description, amount) group.
                        We pick the oldest line as the representative ID so the engine
                        can resolve the group key (description + amount) at match time. */}
                    {Array.from(
                      recurringLines
                        .slice()
                        .sort((a, b) => a.action_date.localeCompare(b.action_date))
                        .reduce((map, line) => {
                          const key = `${line.description.trim().toLowerCase()}||${Math.abs(line.amount)}`;
                          if (!map.has(key)) map.set(key, line); // keep oldest
                          return map;
                        }, new Map<string, typeof recurringLines[0]>())
                        .values()
                    )
                      .sort((a, b) => a.description.localeCompare(b.description, 'he'))
                      .map(line => {
                        const fmt = new Intl.NumberFormat('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                        return (
                          <option key={line.id} value={line.id}>
                            {line.description} — {fmt.format(Math.abs(line.amount))} ₪
                          </option>
                        );
                      })
                    }
                  </select>
                </div>
                <div>
                  <button
                    onClick={handleAddMatchRule}
                    disabled={savingRule || !matchRuleForm.search_text.trim() || !matchRuleForm.target_recurring_line_id}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingRule ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    הוסף חוק
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rules List */}
          {matchRules.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Zap className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-sm">אין חוקי התאמה מוגדרים</p>
              <p className="text-xs mt-1">הוסף חוק כדי להפעיל התאמה אוטומטית בהתאמות בנק</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      טקסט חיפוש
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      פעולה חוזרת מטרה
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      תאריך יצירה
                    </th>
                    {isAdmin && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        פעולות
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matchRules.map(rule => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 rounded text-sm font-mono">
                          <Search className="h-3 w-3" />
                          {rule.search_text}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">
                        {rule.recurring_actions_lines?.description ?? rule.target_recurring_line_id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                        {rule.created_at.split('T')[0]}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleDeleteMatchRule(rule.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            title="מחק חוק"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            מחק
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper function to get Hebrew label for cashflow mapping
function getCashflowMappingLabel(mapping: CashflowMapping): string {
  const labels: Record<CashflowMapping, string> = {
    current_business: 'שוטף - פעילות עסקית',
    financing_loan: 'מימון - פעילות הלוואה',
    intra_group_loan: 'הלוואה בתוך הקבוצה',
    investment: 'השקעה',
    savings: 'חיסכון',
    taxes: 'מיסים',
    inter_account_transfer: 'העברות בין חשבונות החברה',
    personal: 'אישי',
    other: 'אחר',
  };
  return labels[mapping] || mapping;
}

// Helper function to get Hebrew label for frequency type
function getFrequencyTypeLabel(frequencyType: string): string {
  const labels: Record<string, string> = {
    daily: 'יומי',
    weekly_5: 'שבועי (5 ימים)',
    weekly_6: 'שבועי (6 ימים)',
    weekly: 'שבועי',
    monthly: 'חודשי',
    quarterly: 'רבעוני',
    semi_annually: 'חצי שנתי',
    annually: 'שנתי',
    custom: 'מותאם אישית',
  };
  return labels[frequencyType] || frequencyType;
}
